import React, {
  FC,
  ComponentType,
  ComponentProps,
  createContext,
  useState,
} from 'react';
import { UserContextType } from './user.types';
import { USER_ONBOARDING } from '../../../shared/constants/userOnboardingConstants';

export const defaultCurrentUser: UserContextType['currentUser'] = {
  abTestsBuckets: [],
  rights: [],
  wasOnboarded: [],
  featureFlags: [],
  payingOptions: [],
  id: -1,
  firstName: '',
  lastName: '',
  userName: '',
  askPhoneNumber: false,
  hasShared: false,
  plan: '',
  daysRemaining: -1,
  expirationDate: '',
  origExp: '',
  canSubscribe: false,
  pictureUrl: null,
  metier: '',
  phoneNumber: '',
  verified: false,
  lawyerId: '',
  paymentName: '',
  paymentAddress: '',
  paymentZip: '',
  paymentCity: '',
  email: '',
  planFrequency: '',
  canDeleteAccount: false,
  status: 'ANONYMOUS',
  sendWatcherEmail: false,
  sendNewsletter: false,
  sendFoldersWatchersEmail: false,
  sendMarketingEmail: false,
  sendEmails: false,
  sendLegislativeMonitoring: false,
  previouslyExistingUserId: -1,
  name: '',
  isExpired: false,
  isOnboardedOnWatchers: false,
  isOnboardedOnHistory: false,
  isIcp: false,
  firmSize: '',
  isSnapshotsCrawler: false,
  orgData: {
    orgAssignableLicenses: 0,
    orgId: 'ANONYMOUS',
    role: 'MEMBER',
    cbCustomerId: 'ANONYMOUS',
  },
};

export const UserContext = createContext<UserContextType>({
  currentUser: defaultCurrentUser,
  accessPremium: false,
  loggedIn: false,
  setCurrentUserName: () => {},
  setSeeWatchersInbox: () => {},
  setPictureUrl: () => {},
});

type Props = {
  user?: UserContextType['currentUser'];
  accessPremium?: boolean;
  loggedIn?: boolean;
};

export const UserProvider: FC<Props> = ({
  user = defaultCurrentUser,
  accessPremium = false,
  loggedIn = false,
  children,
}) => {
  const [currentUser, setUser] = useState<UserContextType['currentUser']>(user);

  const setCurrentUserName = (firstName: string, lastName: string) => {
    setUser((state) => ({
      ...state,
      firstName,
      lastName,
      userName: `${firstName} ${lastName}`,
      name: firstName,
    }));
  };

  const setSeeWatchersInbox = () => {
    setUser((state) => ({
      ...state,
      wasOnboarded: [...state.wasOnboarded, USER_ONBOARDING.SEE_WATCHERS_INBOX],
      isOnboardedOnWatchers: true,
    }));
  };

  const setPictureUrl = (url: string | null) => {
    setUser((state) => ({
      ...state,
      pictureUrl: url,
    }));
  };

  const value = {
    currentUser,
    setCurrentUserName,
    setSeeWatchersInbox,
    accessPremium,
    loggedIn,
    setPictureUrl,
  };

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

type Subtract<T extends K, K> = Omit<T, keyof K>;

export function withUser<
  P extends ComponentType<ComponentProps<P> & UserContextType>,
  ResolvedProps = JSX.LibraryManagedAttributes<
    P,
    Subtract<React.ComponentProps<P>, UserContextType>
  >
>(WrappedComponent: P) {
  const ComponentWithUser: FC<ResolvedProps> = (props) => (
    <UserContext.Consumer>
      {(userContext) => (
        <WrappedComponent
          {...(props as JSX.LibraryManagedAttributes<
            P,
            React.ComponentProps<P>
          >)}
          {...userContext}
        />
      )}
    </UserContext.Consumer>
  );
  return ComponentWithUser;
}
